import classNames from "classnames";
import BadgesList from "components/BadgesList";
import { ExtractedAnswer } from "components/ExtractedAnswer";
import FullWidthSeparator from "components/FullWidthSeparator";
import RelatedPapers from "components/RelatedPapers/RelatedPapers";
import StudySnapshot from "components/StudySnapshot";
import useCredits from "features/User/hooks/useCredits";
import useUserSettings from "features/User/hooks/useUserSettings";
import { Paper, PaperDetails, RelatedPapersType } from "helpers/api";
import { getLangDir } from "helpers/language";
import { MixpanelFullTextLinkType } from "helpers/services/mixpanel/events";
import useIsPdfLink from "hooks/useIsPdfLink";
import useLabels from "hooks/useLabels";
import { useZoteroMetadata } from "hooks/useZoteroMetadata";
import { useRouter } from "next/router";
import React from "react";
import { CitationDetails } from "./CitationDetails";
import { DetailContentContextProvider } from "./DetailContentContext";
import {
  DetailContentNavigation,
  DetailContentTab,
} from "./DetailContentNavigation";
import { JournalDetails } from "./JournalDetails";
import PaperAuthorsAndPublishDate from "./PaperAuthorsAndPublishDate";
import { PaperLinks } from "./PaperLinks";
import { SnapshotButton } from "./SnapshotButton";
import RetractedTag from "components/Tag/tags/RetractedTag";

function Overview({ paper }: { paper: PaperDetails }) {
  const router = useRouter();
  const { query } = router;
  const [pageLabels, studySnapshotLabel, searchLabels] = useLabels(
    "screens.details",
    "study-snapshot.title",
    "screens.search"
  );

  const [isStudySnapshotExpanded, setIsStudySnapshotExpanded] =
    React.useState(true);
  const { lang } = useUserSettings();
  const { isPremium } = useCredits();

  const extractedText = query["extracted-answer"] as string;
  const abstractText =
    paper.abstract === ""
      ? pageLabels["no-abstract"]
      : (paper.abstract ?? pageLabels["abstract-removed"]);

  return (
    <section>
      <p className="my-6 text-lg font-bold">{pageLabels["abstract"]}</p>
      <p className="mb-3 text-base whitespace-pre-line" data-testid="abstract">
        {abstractText}
      </p>
      {paper.badges && <BadgesList badges={paper.badges} />}
      {extractedText ? (
        <div className="my-6">
          <ExtractedAnswer
            q={query["q"] as string}
            answer={extractedText}
            dir={getLangDir(lang)}
          />
        </div>
      ) : null}

      <FullWidthSeparator />
      <div className="my-6">
        <div className="flex flex-row gap-2 justify-between items-center mb-3">
          <span className="text-lg font-bold">{studySnapshotLabel}</span>
          <SnapshotButton
            isPremium={isPremium}
            isStudySnapshotExpanded={isStudySnapshotExpanded}
            setIsStudySnapshotExpanded={setIsStudySnapshotExpanded}
            searchLabels={searchLabels}
          />
        </div>
        <p className="my-6 text-lg text-fg-base">{paper.abstract_takeaway}</p>
        <div
          className={classNames({
            "sparkle-box": isStudySnapshotExpanded,
            hidden: !isStudySnapshotExpanded,
          })}
        >
          <StudySnapshot
            paperId={paper.id}
            badges={paper.badges}
            fetchOnMount={false}
            legacyDesign
          />
        </div>
        {!isStudySnapshotExpanded && (
          <p className="my-6 text-sm italic text-fg-muted">
            {searchLabels["expand-study-snapshot-desc"]}
          </p>
        )}
      </div>

      <FullWidthSeparator />
    </section>
  );
}

export type DetailContentProps = {
  handleClickFullTextLink: (linkType: MixpanelFullTextLinkType) => void;
  handleClickSave: (paper: Paper) => void;
  handleClickCite?: () => void;
  handleClickShare?: () => void;
  isBookmarked?: boolean;
  paper: PaperDetails;
  hideSemanticScholarButton?: boolean;
  hideCitations?: boolean;
  hideReferences?: boolean;
  hideSJR?: boolean;
  isUploadedPaper?: boolean;
  onDelete?: () => void;
};

function DetailContent({
  handleClickFullTextLink,
  handleClickSave,
  handleClickCite,
  handleClickShare,
  isBookmarked,
  paper,
  hideSemanticScholarButton,
  hideCitations,
  hideReferences,
  hideSJR,
  isUploadedPaper,
  onDelete,
}: DetailContentProps) {
  const [selectedTab, setSelectedTab] =
    React.useState<DetailContentTab>("overview");

  const [searchLabels] = useLabels("screens.search");
  const { isPdfLink } = useIsPdfLink(paper?.open_access_pdf_url);

  const showAskPaperButton = isPdfLink || isUploadedPaper;
  const showFullTextAnalysis = showAskPaperButton;
  useZoteroMetadata(paper.doi);

  return (
    <DetailContentContextProvider>
      {paper.is_retracted && (
        <div className="mb-6">
          <RetractedTag />
        </div>
      )}
      <section className="pb-6 border-border-base">
        <h1
          data-testid="paper-title"
          className="sm:h1-base h2-base text-fg-base leading-[normal]"
        >
          {paper.is_retracted ? searchLabels["retracted"] : ""}
          {paper.title}
        </h1>
        <PaperAuthorsAndPublishDate paper={paper} />
        <div className="flex flex-wrap gap-4 justify-between mt-6">
          <div className="sm:w-[50%] w-full">
            <JournalDetails journal={paper.journal} hideSJR={hideSJR} />
          </div>
          {!hideCitations && <CitationDetails paper={paper} />}
        </div>
        <div className="flex gap-4 mt-6">
          <PaperLinks
            handleClickFullTextLink={handleClickFullTextLink}
            handleClickSave={handleClickSave}
            handleClickCite={handleClickCite}
            handleClickShare={handleClickShare}
            isBookmarked={isBookmarked}
            paper={paper}
            hideSemanticScholarButton={hideSemanticScholarButton}
            onDelete={onDelete}
          />
        </div>
      </section>

      <DetailContentNavigation
        selectedTab={selectedTab}
        onTabClick={setSelectedTab}
        showFullTextAnalysis={showFullTextAnalysis}
        hideCitations={hideCitations}
        hideReferences={hideReferences}
      />

      <section
        className={classNames({
          hidden: selectedTab !== "overview",
        })}
      >
        <Overview paper={paper} />
      </section>

      <section
        className={classNames({
          hidden: selectedTab !== "full-text-analysis",
        })}
      >
        <p className="text-fg-muted">Full text analysis coming soon...</p>
      </section>

      <section
        className={classNames({
          hidden: selectedTab !== "references",
        })}
      >
        <RelatedPapers
          id={paper.id}
          handleClickSave={handleClickSave}
          relatedPaperType={RelatedPapersType.REFERENCES}
        />
      </section>

      <section
        className={classNames({
          hidden: selectedTab !== "citations",
        })}
      >
        <RelatedPapers
          id={paper.id}
          handleClickSave={handleClickSave}
          relatedPaperType={RelatedPapersType.CITATIONS}
        />
      </section>
    </DetailContentContextProvider>
  );
}

export default DetailContent;
